import { BRANDHUB_GENERATE_DOMAIN_LIST_KEY } from '@brandhub/hooks/brandhubQueries';
import {
  deleteDomainContactList,
  getDomainContactListContacts,
  importDomainContactList,
  patchDomainContactList,
} from '@brandhub/services/brandhubService';
import {
  type DomainContactListImportErrorResponse,
  type DomainContactListImportResponse,
  type GenerateDomainListContactsStatus,
  getDomainContactListImportBaseErrorMessages,
} from '@brandhub/types/brandhub';
import {
  DuplicateContactsSelectionCancelledError,
  useResolveDuplicatesBeforeAdding,
} from '@components/duplicate-contacts-modal/useResolveDuplicatesBeforeAdding';
import { useNotifications } from '@components/Notifications.tsx';
import { trackGaEvent } from '@lib/trackGaEvent';
import { createMailing, updateRecipients } from '@mailings/services/mailings/mailingsService';
import { MEDIA_DATABASE_SEARCH_KEY } from '@media-database/hooks/mediaDatabaseQueries';
import {
  addContactsToLists,
  checkExportLimits,
  countUniqueContactsInMediaLists,
  createMediaList,
  createMediaListWithUniqueName,
  exportContacts,
  getAllMediaLists,
  getDuplicatedContacts,
  getMediaContacts,
  getMediaListDetails,
  getRecentlyAddedToLists,
  importContacts,
  removeContactsFromMediaLists,
  removeMediaList,
  removeMediaLists,
  updateMediaList,
  uploadFileWithContacts,
  validateContactsImport,
  validateMediaListName,
} from '@media-lists/services/mediaListsService';
import {
  AddToListsPayload,
  CountUniqueContactsInMediaListsResponse,
  CreateMediaContactListPayload,
  MediaContactList,
  MediaContactListFetchQueryParams,
  MediaContactListResponse,
  MediaContactsFetchQueryParams,
  MediaContactsResponse,
  RecentlyAddedToListsResponse,
} from '@media-lists/types/mediaContactList';
import { resolveDomainContactListIdForImport } from '@media-lists/utils/domainListContactsStatus';
import { accountQueries, invalidateSubscriptionQuery } from '@src/hooks/queries/accountQueries';
import { useSessionStorage } from '@src/hooks/useSessionStorage';
import { ContactBeingAdded, DuplicatedContactsResponse } from '@src/types/duplicateContacts';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AxiosError, isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

const MAX_MEDIA_LIST_NAME_LENGTH = 100;
const INFINITE_SCROLL_MEDIA_LIST_LIMIT = 25;

export const MEDIA_LIST_BASE_KEY = 'media-lists';
export const AI_SUGGESTED_DOMAIN_CONTACT_LIST_KEY = [
  MEDIA_LIST_BASE_KEY,
  'aiSuggestedDomainContactList',
] as const;

export const aiSuggestedDomainContactListQueriesPrefix = (listId: number) =>
  [...AI_SUGGESTED_DOMAIN_CONTACT_LIST_KEY, listId] as const;
export const MEDIA_LIST_LIST_BASE_KEYS = [MEDIA_LIST_BASE_KEY, 'list'];
export const MEDIA_LIST_LIST_INFINITE_KEYS = [MEDIA_LIST_BASE_KEY, 'list', 'infinite'];
export const MEDIA_LIST_CONTACTS_BASE_KEYS = [MEDIA_LIST_BASE_KEY, 'contacts'];

export const useMediaListsKeys = {
  list: (params: MediaContactListFetchQueryParams) =>
    queryOptions<MediaContactListResponse>({
      queryKey: [...MEDIA_LIST_LIST_BASE_KEYS, params] as const,
      queryFn: () => getAllMediaLists(params),
      placeholderData: keepPreviousData,
    }),
  infiniteList: (params: MediaContactListFetchQueryParams) =>
    infiniteQueryOptions({
      queryKey: [...MEDIA_LIST_LIST_INFINITE_KEYS, params],
      queryFn: ({ pageParam }) =>
        getAllMediaLists({ page: pageParam, ...params, limit: INFINITE_SCROLL_MEDIA_LIST_LIMIT }),
      initialPageParam: 1,
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
      getNextPageParam: ({ pagination }) => {
        const { currentPage, pageItems, totalCount } = pagination;
        if (!currentPage || !pageItems || !totalCount) {
          return undefined;
        }
        return currentPage * pageItems < totalCount ? currentPage + 1 : undefined;
      },
    }),
  dashboardList: (params: MediaContactListFetchQueryParams) =>
    queryOptions<MediaContactListResponse>({
      queryKey: [...MEDIA_LIST_LIST_BASE_KEYS, 'dashboardMediaLists', params] as const,
      queryFn: () => getAllMediaLists(params),
    }),
  byIds: (ids: number[]) =>
    queryOptions<MediaContactListResponse>({
      queryKey: [...MEDIA_LIST_LIST_BASE_KEYS, 'byIds', ids] as const,
      queryFn: () =>
        getAllMediaLists({
          ids,
          limit: ids.length,
        }),
      enabled: ids.length > 0,
    }),
  show: (id: MediaContactList['id']) =>
    queryOptions<MediaContactList, AxiosError>({
      queryKey: [MEDIA_LIST_BASE_KEY, id] as const,
      queryFn: () => getMediaListDetails(String(id)),
    }),
  contacts: (id: MediaContactList['id'], params: MediaContactsFetchQueryParams) =>
    queryOptions<MediaContactsResponse>({
      queryKey: [...MEDIA_LIST_CONTACTS_BASE_KEYS, id, params] as const,
      queryFn: () => getMediaContacts(String(id), params),
      placeholderData: keepPreviousData,
    }),
  aiSuggestedDomainContactListContacts: (listId: number, params: MediaContactsFetchQueryParams) =>
    queryOptions({
      queryKey: [...aiSuggestedDomainContactListQueriesPrefix(listId), params] as const,
      queryFn: () => getDomainContactListContacts(listId, params),
      placeholderData: keepPreviousData,
      enabled: Number.isFinite(listId) && listId > 0,
    }),
  validateName: (mediaListName: string, previousMediaListName?: string) =>
    queryOptions({
      queryKey: [MEDIA_LIST_BASE_KEY, mediaListName] as const,
      queryFn: () => validateMediaListName(mediaListName),
      retry: false,
      enabled:
        mediaListName.length > 1 &&
        mediaListName.length <= MAX_MEDIA_LIST_NAME_LENGTH &&
        mediaListName !== previousMediaListName,
    }),
  countUniqueContacts: (mediaLists: MediaContactList[]) =>
    queryOptions<CountUniqueContactsInMediaListsResponse>({
      queryKey: [
        MEDIA_LIST_BASE_KEY,
        'countUniqueContacts',
        {
          mediaLists: mediaLists.map(({ id, contactsCount }) => ({
            id,
            contactsCount,
          })),
        },
      ] as const,
      queryFn: () => countUniqueContactsInMediaLists({ mediaLists }),
    }),
  duplicatedContacts: (mediaLists: MediaContactList[]) =>
    queryOptions<DuplicatedContactsResponse>({
      queryKey: [
        MEDIA_LIST_BASE_KEY,
        'duplicatedContacts',
        {
          mediaLists: mediaLists.map(({ id, contactsCount }) => ({
            id,
            contactsCount,
          })),
        },
      ] as const,
      queryFn: () => getDuplicatedContacts({ mediaLists }),
      placeholderData: keepPreviousData,
    }),
  recentlyAddedToLists: (contactId: string | null, isMediaListContact: boolean) =>
    queryOptions<RecentlyAddedToListsResponse>({
      queryKey: [MEDIA_LIST_LIST_BASE_KEYS, contactId, 'recentlyAddedToLists'] as const,
      queryFn: !!contactId ? () => getRecentlyAddedToLists(contactId) : skipToken,
      enabled: isMediaListContact && !!contactId,
    }),
};

export const useFetchMediaLists = (params: MediaContactListFetchQueryParams) => {
  return useQuery({ ...useMediaListsKeys.list(params) });
};

export const useInfiniteFetchMediaLists = (
  params?: MediaContactListFetchQueryParams,
  options: { refetchOnMount: boolean } = { refetchOnMount: false },
) => {
  return useInfiniteQuery({
    ...useMediaListsKeys.infiniteList(params ?? {}),
    refetchOnMount: options.refetchOnMount,
  });
};

export const useMediaListNameValidationQuery = (
  mediaListName: string = '',
  previousMediaListName?: string,
) => {
  return useQuery(useMediaListsKeys.validateName(mediaListName, previousMediaListName));
};

export const useMediaListDetailsQuery = (
  mediaListId: number,
  options?: Omit<UseQueryOptions<MediaContactList, AxiosError>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({ ...useMediaListsKeys.show(mediaListId), ...options });
};

export const useMediaContactsListQuery = (
  mediaListId: number,
  params: MediaContactsFetchQueryParams,
) => {
  return useQuery(useMediaListsKeys.contacts(mediaListId, params));
};

export const useCreateNewMediaListMutation = () => {
  const { t } = useTranslation(['mediaList']);
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateMediaContactListPayload) => createMediaList(payload),
    onError: () => {
      showErrorNotification(t('mediaList:failedToCreateList'));
    },
    onSuccess: async () => {
      showSuccessNotification(t('mediaList:listCreated'));

      await queryClient.invalidateQueries({ queryKey: MEDIA_LIST_LIST_BASE_KEYS });
      void queryClient.resetQueries({ queryKey: MEDIA_LIST_LIST_INFINITE_KEYS });
      trackGaEvent('create', { item_label: 'media_list_create' });

      queryClient.setQueryData(accountQueries.current().queryKey, (account) =>
        account
          ? {
              ...account,
              mediaListExists: true,
            }
          : undefined,
      );
    },
  });
};

export const useCreateMediaListWithUniqueNameMutation = () => {
  const { t } = useTranslation(['mediaList']);
  const { showErrorNotification } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateMediaContactListPayload) => createMediaListWithUniqueName(payload),
    onError: () => {
      showErrorNotification(t('mediaList:failedToCreateList'));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: MEDIA_LIST_LIST_BASE_KEYS });
      void queryClient.resetQueries({ queryKey: MEDIA_LIST_LIST_INFINITE_KEYS });
      trackGaEvent('create', { item_label: 'media_list_create' });

      queryClient.setQueryData(accountQueries.current().queryKey, (account) =>
        account
          ? {
              ...account,
              mediaListExists: true,
            }
          : undefined,
      );
    },
  });
};

export const useDeleteMediaListQuery = () => {
  const { t } = useTranslation(['common', 'mediaList']);
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mediaListId: string) => removeMediaList(mediaListId),
    onError: () => {
      showErrorNotification(t('mediaList:failedToDeleteList'));
    },
    onSuccess: async () => {
      try {
        void queryClient.resetQueries({ queryKey: accountQueries.current().queryKey });
        await queryClient.invalidateQueries({ queryKey: MEDIA_LIST_LIST_BASE_KEYS });
        await queryClient.refetchQueries({ queryKey: MEDIA_LIST_LIST_INFINITE_KEYS });
        await navigate({ to: '/pr-toolkit/media-lists' });
        showSuccessNotification(t('mediaList:listDeleted'));
      } catch {
        showErrorNotification(t('common:somethingWentWrong'));
      }
    },
  });
};

export const useDeleteDomainContactListMutation = (options?: {
  successNotificationMessage?: string;
}) => {
  const { t } = useTranslation(['common', 'mediaList']);
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const successNotificationMessage =
    options?.successNotificationMessage ?? t('mediaList:listDeleted');

  return useMutation({
    mutationFn: (domainContactListId: number) => deleteDomainContactList(domainContactListId),
    onError: () => {
      showErrorNotification(t('common:somethingWentWrong'));
    },
    onSuccess: async (_data, domainContactListId) => {
      try {
        queryClient.setQueriesData<GenerateDomainListContactsStatus>(
          { queryKey: BRANDHUB_GENERATE_DOMAIN_LIST_KEY },
          (old: GenerateDomainListContactsStatus | undefined) =>
            old ? { ...old, smartGroup: undefined } : undefined,
        );
        void queryClient.resetQueries({ queryKey: accountQueries.current().queryKey });
        const aiListQueryKey = aiSuggestedDomainContactListQueriesPrefix(domainContactListId);
        await queryClient.cancelQueries({ queryKey: aiListQueryKey });
        queryClient.removeQueries({ queryKey: aiListQueryKey });
        await queryClient.invalidateQueries({ queryKey: MEDIA_LIST_LIST_BASE_KEYS });
        await queryClient.invalidateQueries({
          queryKey: BRANDHUB_GENERATE_DOMAIN_LIST_KEY,
        });
        await queryClient.refetchQueries({ queryKey: MEDIA_LIST_LIST_INFINITE_KEYS });
        await navigate({ to: '/pr-toolkit/media-lists' });
        showSuccessNotification(successNotificationMessage);
        trackGaEvent('delete', {
          item_label: 'aiSuggestion_dismiss_suggested_list',
          event_source: 'media_lists',
        });
      } catch {
        showErrorNotification(t('common:somethingWentWrong'));
      }
    },
  });
};

export const useDeleteMultipleMediaListsQuery = () => {
  const { t } = useTranslation(['mediaList']);
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: number[]) => removeMediaLists(ids),
    onError: () => {
      showErrorNotification(t('mediaList:mediaListsRemovedFail'));
    },
    onSuccess: async () => {
      void queryClient.resetQueries({ queryKey: accountQueries.current().queryKey });
      await queryClient.refetchQueries({ queryKey: MEDIA_LIST_LIST_INFINITE_KEYS });
      await queryClient.invalidateQueries({ queryKey: MEDIA_LIST_LIST_BASE_KEYS });
      showSuccessNotification(t('mediaList:mediaListsRemoved'));
    },
  });
};

export const useUpdateMediaListQuery = (mediaListId: number) => {
  const { t } = useTranslation(['mediaList']);
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mediaList: Partial<MediaContactList>) => updateMediaList(mediaListId, mediaList),
    onError: () => {
      showErrorNotification(t('mediaList:failedToUpdateList'));
    },
    onSuccess: async () => {
      void queryClient.invalidateQueries({ queryKey: MEDIA_LIST_LIST_INFINITE_KEYS });
      await queryClient
        .invalidateQueries(useMediaListsKeys.show(mediaListId))
        .then(() => showSuccessNotification(t('mediaList:listUpdated')));
    },
  });
};

export const useUploadContactsQuery = (mediaListId: string, errorFn?: () => void) => {
  const { t } = useTranslation(['mediaListImport']);
  const { showErrorNotification } = useNotifications();
  const navigate = useNavigate();
  const [, setUploadedContactsFileResponse] = useSessionStorage({
    key: 'uploadedContactsFileResponse',
    defaultValue: null,
  });

  return useMutation({
    mutationFn: (payload: FormData) => uploadFileWithContacts(payload),
    onSuccess: async (data) => {
      setUploadedContactsFileResponse(data);
      await navigate({
        to: '/pr-toolkit/media-lists/$mediaListId/import',
        params: { mediaListId },
      });
    },
    onError: () => {
      if (errorFn) {
        errorFn();
      }
      showErrorNotification(t('mediaListImport:failedToImportFile'));
    },
  });
};

export const useAiSuggestedDomainContactListContactsQuery = (
  listId: number,
  params: MediaContactsFetchQueryParams,
) => {
  return useQuery(useMediaListsKeys.aiSuggestedDomainContactListContacts(listId, params));
};

export const usePatchDomainContactListMutation = (listId: number) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['mediaList']);
  const { showErrorNotification } = useNotifications();

  return useMutation({
    mutationFn: (contactAnewstipIds: string[]) =>
      patchDomainContactList(listId, { contactAnewstipIds }),
    onError: () => {
      showErrorNotification(t('mediaList:failedToDeleteContact'));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: aiSuggestedDomainContactListQueriesPrefix(listId),
      });
    },
  });
};

export const useDeleteMultipleContactsQuery = () => {
  const { t } = useTranslation(['mediaList']);
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      selectedContactsIds,
      mediaListId,
    }: {
      selectedContactsIds: number[];
      mediaListId: string;
    }) => removeContactsFromMediaLists(selectedContactsIds, mediaListId),
    onError: () => {
      showErrorNotification(t('mediaList:failedToDeleteContact'));
    },
    onSuccess: async (_, { selectedContactsIds }) => {
      await queryClient.invalidateQueries({
        queryKey: [MEDIA_LIST_BASE_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: MEDIA_DATABASE_SEARCH_KEY,
      });
      showSuccessNotification(
        t('mediaList:contactsRemovedFromList', { count: selectedContactsIds.length }),
      );
    },
  });
};

export const useAddContactsToLists = (onSuccess?: () => void) => {
  const { t } = useTranslation(['mediaDatabase']);
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const resolveDuplicatesBeforeAdding = useResolveDuplicatesBeforeAdding();
  const queryClient = useQueryClient();

  const completeAddContactsToListsSuccess = (ids: number[], addedContactsSize?: number) => {
    onSuccess?.();
    showSuccessNotification(
      t('mediaDatabase:contactAddedSuccessfully', { count: addedContactsSize ?? ids.length }),
    );
    void queryClient.invalidateQueries({ queryKey: [MEDIA_LIST_BASE_KEY] });
    void queryClient.invalidateQueries({ queryKey: MEDIA_DATABASE_SEARCH_KEY });
  };

  return useMutation({
    mutationFn: async ({
      ids,
      contactsBeingAdded,
      mediaListsId,
    }: AddToListsPayload & { contactsBeingAdded: ContactBeingAdded[] }) => {
      const decision = await resolveDuplicatesBeforeAdding({
        contactsBeingAdded,
        mediaListsId,
      });

      if (decision.isCancelled) {
        throw new DuplicateContactsSelectionCancelledError();
      }

      const idsToAdd = ids.filter((id) => !decision.contactIdsToSkip.has(id));

      if (idsToAdd.length === 0) {
        return { addedContactsSize: 0 };
      }

      const response = await addContactsToLists({ ids: idsToAdd, mediaListsId });

      return { addedContactsSize: response.addedContactsSize ?? idsToAdd.length };
    },
    onError: (error, { ids }) => {
      if (error instanceof DuplicateContactsSelectionCancelledError) {
        return;
      }

      showErrorNotification(t('mediaDatabase:failedToAddToList', { count: ids.length }));
    },
    onSuccess: (response, { ids }) => {
      completeAddContactsToListsSuccess(ids, response.addedContactsSize);
    },
  });
};

export const useImportContacts = () => {
  const { t } = useTranslation(['mediaListImport']);
  const { showErrorNotification } = useNotifications();

  return useMutation({
    mutationFn: importContacts,
    onSuccess: () => {
      trackGaEvent('add_item', {
        item_label: 'add_contacts_to_media_list',
        event_source: 'import',
      });
    },
    onError: () => {
      showErrorNotification(t('mediaListImport:importFailure'));
    },
  });
};

export const useExportContacts = () => {
  const { t } = useTranslation(['mediaListExport']);
  const { showErrorNotification } = useNotifications();

  return useMutation({
    mutationFn: exportContacts,
    onSuccess: () => {
      invalidateSubscriptionQuery();
    },
    onError: () => {
      showErrorNotification(t('mediaListExport:exportFailure'));
    },
  });
};

export const useCheckExportLimits = () => {
  const { t } = useTranslation(['mediaListExport']);
  const { showErrorNotification } = useNotifications();

  return useMutation({
    mutationFn: checkExportLimits,
    onError: () => {
      showErrorNotification(t('mediaListExport:exportFailure'));
    },
  });
};

export const useValidateContactsImport = () => {
  const { t } = useTranslation(['mediaListImport']);
  const { showErrorNotification } = useNotifications();

  return useMutation({
    mutationFn: validateContactsImport,
    onError: () => {
      showErrorNotification(t('mediaListImport:importFailure'));
    },
  });
};

export const useGetUniqueContactsCountQuery = (
  mediaLists: MediaContactList[],
  options?: Omit<UseQueryOptions<CountUniqueContactsInMediaListsResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    ...useMediaListsKeys.countUniqueContacts(mediaLists),
    ...options,
  });
};

export const useGetDuplicatedContactsQuery = (mediaLists: MediaContactList[]) => {
  return useQuery({
    ...useMediaListsKeys.duplicatedContacts(mediaLists),
    enabled: mediaLists.length > 0,
  });
};

export const useGetRecentlyAddedToListsQuery = (
  contactId: number | null,
  isMediaListContact: boolean,
) => {
  return useQuery({
    ...useMediaListsKeys.recentlyAddedToLists(String(contactId), isMediaListContact),
  });
};

export interface SaveSuggestedMediaListVariables {
  mediaList: MediaContactList;
  skipRedirect?: boolean;
}

const SAVE_SUGGESTED_IMPORT_API_MESSAGE_I18N: Record<string, string> = {
  'Temporary list already imported':
    'mediaList:saveSuggestedImportErrorTemporaryListAlreadyImported',
  'Not enough limits': 'mediaList:saveSuggestedImportErrorNotEnoughLimits',
  'Contacts limit used': 'mediaList:saveSuggestedImportErrorContactsLimitUsed',
};

function notificationMessageForSaveSuggestedImportError(
  error: unknown,
  t: (key: string) => string,
): string {
  if (!isAxiosError(error)) {
    return t('mediaList:failedToSaveSuggestedList');
  }
  const data = error.response?.data as unknown;
  const apiMessages = getDomainContactListImportBaseErrorMessages(data);
  const first = apiMessages[0];
  if (!first) {
    return t('mediaList:failedToSaveSuggestedList');
  }
  const i18nKey = SAVE_SUGGESTED_IMPORT_API_MESSAGE_I18N[first];
  return i18nKey ? t(i18nKey) : t('mediaList:failedToSaveSuggestedList');
}

export const useSaveSuggestedMediaListMutation = () => {
  const { t } = useTranslation(['mediaList']);
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<
    DomainContactListImportResponse,
    AxiosError<DomainContactListImportErrorResponse>,
    SaveSuggestedMediaListVariables
  >({
    mutationFn: ({ mediaList }: SaveSuggestedMediaListVariables) =>
      importDomainContactList(resolveDomainContactListIdForImport(mediaList)),
    onError: (error) => {
      showErrorNotification(
        notificationMessageForSaveSuggestedImportError(error, t as (key: string) => string),
      );
    },
    onSuccess: async (data, { mediaList, skipRedirect }) => {
      showSuccessNotification(t('mediaList:suggestedListSaved'));

      await queryClient.invalidateQueries({ queryKey: MEDIA_LIST_LIST_BASE_KEYS });
      void queryClient.resetQueries({ queryKey: MEDIA_LIST_LIST_INFINITE_KEYS });
      trackGaEvent('create', { item_label: 'media_list_save_suggested' });

      const newSmartGroupId = data.contactSmartGroup.id;
      await queryClient.invalidateQueries({ queryKey: [MEDIA_LIST_BASE_KEY, newSmartGroupId] });
      await queryClient.invalidateQueries({
        queryKey: aiSuggestedDomainContactListQueriesPrefix(
          resolveDomainContactListIdForImport(mediaList),
        ),
      });
      invalidateSubscriptionQuery();

      if (!skipRedirect) {
        await navigate({
          to: '/pr-toolkit/media-lists/$mediaListId',
          params: { mediaListId: `${newSmartGroupId}` },
          search: {},
        });
      }
      trackGaEvent('add_item', {
        item_label: 'aiSuggestion_save_suggested_list',
        event_source: 'media_lists',
      });
    },
  });
};

export const useSendMailingToMediaListsMutation = (aiVisibilitySearchId?: string) => {
  const { t } = useTranslation(['mediaList', 'mailings']);
  const { showErrorNotification } = useNotifications();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({
      mediaListIds,
      brandHubDomainId,
    }: {
      mediaListIds: number[];
      brandHubDomainId?: number;
    }) =>
      createMailing({
        title: t('mailings:initialTitleValue'),
        content: t('mailings:initialContentValue'),
        aiVisibilitySearchId,
        brandHubDomainId,
      }).then(({ mailing }) => {
        trackGaEvent('create', {
          item_label: 'mailing_create',
          type: 'general',
        });

        return updateRecipients({
          mailingId: mailing.id,
          selectedMediaListIds: mediaListIds,
          excludedContactIds: [],
        }).then(() => {
          trackGaEvent('edit', {
            item_label: 'mailing_add_media_lists',
            type: 'general',
          });

          return mailing.id;
        });
      }),
    onSuccess: async (mailingId) => {
      await navigate({
        to: '/pr-toolkit/emails/$mailingId/edit',
        params: { mailingId: `${mailingId}` },
        search: { from: 'media-list' },
      });
    },
    onError: () => {
      showErrorNotification(t('mediaList:failedToPrepareMailing'));
    },
  });
};

export const useMediaListsForDashboardQuery = (params: MediaContactListFetchQueryParams) => {
  return useQuery({
    ...useMediaListsKeys.dashboardList(params),
  });
};
